/* eslint-disable import/order */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, func, shape, string } from 'prop-types';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import * as R from 'ramda';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

// components
import { MAutocomplete, MDropdown } from 'common';
import PillsSection from 'feature/loading-management/create-new-common/PillSection';

// utils
import { useGetAffiliateOptions } from 'hooks';
import { capitalize } from 'utils/helpers/format';
import { findOptionByValue } from 'utils/helpers/function';
import { validateRequiredAutocomplete } from 'utils/helpers/formValidations';
import { CARRIER_AUTH_BY_OPTIONS } from 'feature/loading-management/utils/const';
import { userRoles } from 'utils/currentUserRoles';
import { dividerStyles } from '../Steps.styles';

const renderEllipsisTooltipCell = cellParams => (
  <Tooltip arrow placement="bottom" title={cellParams.value}>
    <Box
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }}
    >
      {cellParams.value}
    </Box>
  </Tooltip>
);

const Detail = ({
  authorizationBy,
  currentSelection,
  detailSearchTerm,
  fetchViewByList,
  setAuthorizationBy,
  setDetailSearchTerm,
  setHasUnsavedChanges,
  setParams,
  setSelectStepSearchTerm,
  setWizardDetails,
  params,
  supplier,
  viewByList,
  viewByListOptions,
}) => {
  const theme = useTheme();

  const authSetupTitleStyles = {
    color: theme.palette.text.primary,
    fontFamily: 'Gothic A1',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: '0.5px',
    lineHeight: '150%',
  };

  const authSetupDescriptionStyles = {
    color: theme.palette.text.secondary,
    fontFamily: 'Overpass',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: '0.07px',
    lineHeight: 'normal',
    mb: 3,
  };

  const initialRender = useRef(true);
  const [validation, setValidation] = useState('');
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const quickFilterInput = detailSearchTerm;
  const { pathname } = useLocation();
  const routePath = pathname.split('/');
  const onCarrierAuth = pathname.includes('carrier-authorizations');
  const viewBy = routePath[5];
  const isCreateCarrierAuthEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_CARRIER_AUTHORIZATIONS
  );

  const affiliatesOptions = useGetAffiliateOptions([userRoles.SUPPLIER]);
  const sortedAffiliatesOptions = [...affiliatesOptions].sort((a, b) =>
    `${a.value}`.localeCompare(`${b.value}`, undefined, { numeric: true, sensitivity: 'base' })
  );

  const isCarrierMode = authorizationBy === 'carrier';
  const selectedRowId = params.viewById ?? null;
  const selectedViewByListOption = findOptionByValue(params.viewById, viewByListOptions);

  const rows = viewByList.map((item, index) => {
    if (isCarrierMode) {
      const fein = item.carrierFEIN || item.fein || '';
      const feinFormatted =
        fein && fein.length === 9 ? `${fein.slice(0, 2)}-${fein.slice(2)}` : fein;
      const carrierRowId =
        item.carrierId || item.id || `carrier-${item.scac || item.carrierSCAC || 'row'}-${index}`;

      return {
        carrierName: item.carrierName || '--',
        fein: feinFormatted || '--',
        id: carrierRowId,
        scac: item.scac || item.carrierSCAC || '--',
      };
    }

    const locationLongName = item.nameLong || item.nameShort || '';
    const locationRowId = item.locationId ?? item.id ?? `location-${index}`;
    return {
      id: locationRowId,
      locationDisplay: locationLongName,
    };
  });

  const columns = isCarrierMode
    ? [
        {
          cellClassName: 'carrier-name-cell',
          field: 'carrierName',
          headerClassName: 'carrier-name-header',
          headerName: 'Carrier',
          maxWidth: 450,
          minWidth: 160,
          renderCell: renderEllipsisTooltipCell,
          width: 450,
        },
        {
          align: 'center',
          cellClassName: 'scac-cell',
          field: 'scac',
          headerAlign: 'center',
          headerName: 'SCAC',
          maxWidth: 80,
          minWidth: 60,
          width: 80,
        },
        {
          align: 'center',
          cellClassName: 'fein-cell',
          field: 'fein',
          headerAlign: 'center',
          headerName: 'FEIN',
          maxWidth: 130,
          minWidth: 110,
          width: 130,
        },
      ]
    : [
        {
          field: 'locationDisplay',
          flex: 1,
          headerName: 'Location',
          minWidth: 220,
          renderCell: renderEllipsisTooltipCell,
        },
      ];

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(v => v.trim())
          .filter(Boolean)
      );
    }, 200);

    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  const filterModel = useMemo(
    () => ({
      items: [],
      quickFilterValues,
    }),
    [quickFilterValues]
  );

  useEffect(() => {
    if (!supplier?.value && sortedAffiliatesOptions.length) {
      setWizardDetails({
        name: 'supplier',
        value: sortedAffiliatesOptions[0],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedAffiliatesOptions.length, supplier?.value]);

  useEffect(() => {
    if (supplier.value) {
      fetchViewByList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationBy, supplier.value]);

  useEffect(() => {
    if (onCarrierAuth && !R.isEmpty(currentSelection) && !params.viewById) {
      setParams({ viewById: currentSelection[`${viewBy}Id`] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTypeChange = useCallback(
    value => {
      setWizardDetails({ name: 'authorizationBy', value });
      setAuthorizationBy(value);
      setParams({ viewById: null });
      setDetailSearchTerm('');
      setSelectStepSearchTerm('');
      setHasUnsavedChanges(true);
    },
    [
      setAuthorizationBy,
      setDetailSearchTerm,
      setHasUnsavedChanges,
      setParams,
      setSelectStepSearchTerm,
      setWizardDetails,
    ]
  );

  const handleTypeChange = (event, radioValue) => {
    const value = radioValue ?? event?.target?.value ?? event?.value ?? event;

    if (!value || value === authorizationBy) {
      return;
    }

    applyTypeChange(value);
  };

  const handleAffiliateChange = ({ name, value }) => {
    const selectedAffiliate = findOptionByValue(value, sortedAffiliatesOptions);
    if (!selectedAffiliate || selectedAffiliate.value === supplier?.value) {
      return;
    }

    if (initialRender.current) {
      initialRender.current = false;
    }

    setHasUnsavedChanges(true);
    setParams({ viewById: null });
    setSelectStepSearchTerm('');
    setWizardDetails({ name, value: { ...selectedAffiliate } });
  };

  const handleOnChange = val => {
    const selectedValue = R.prop('value', val);
    setParams({ viewById: selectedValue });
    setValidation(validateRequiredAutocomplete(selectedValue));
    if (selectedValue) {
      setHasUnsavedChanges(true);
    }
  };

  const handleSelection = useCallback(
    rowSelectionModel => {
      const [selection] = rowSelectionModel;
      setParams({ viewById: selection ?? null });
      if (selection) {
        setHasUnsavedChanges(true);
      }
    },
    [setHasUnsavedChanges, setParams]
  );

  const handleRadioSelection = useCallback(
    (event, rowId) => {
      event.stopPropagation();
      handleSelection([rowId]);
    },
    [handleSelection]
  );

  const selectionColumn = {
    align: 'center',
    field: 'selection',
    headerName: '',
    maxWidth: 36,
    minWidth: 36,
    pinnable: false,
    renderCell: cellParams => (
      <Radio
        checked={cellParams.id === selectedRowId}
        onChange={event => handleRadioSelection(event, cellParams.id)}
        size="small"
        slotProps={{
          input: {
            'aria-label': `Select ${cellParams.row.carrierName || cellParams.row.locationDisplay}`,
          },
        }}
        sx={{
          '&.Mui-checked': {
            color: 'primary.main',
          },
        }}
      />
    ),
    sortable: false,
    width: 36,
  };

  const gridColumns = [selectionColumn, ...columns];

  const handleClearSearch = () => {
    setDetailSearchTerm('');
  };

  return (
    <Box display="flex" flexDirection="column" minHeight={0} sx={{ p: 2, px: 3 }}>
      {isCreateCarrierAuthEnabled && (
        <Typography sx={{ ...authSetupTitleStyles, mt: 2 }} variant="body1">
          Authorization Setup Type
        </Typography>
      )}
      {!isCreateCarrierAuthEnabled && (
        <Typography variant="body1">How do you want to select authorizations?</Typography>
      )}

      {isCreateCarrierAuthEnabled ? (
        <FormControl>
          <RadioGroup
            name="authorizationBy"
            onChange={handleTypeChange}
            sx={{ ml: 1, mt: 1 }}
            value={authorizationBy}
          >
            <FormControlLabel control={<Radio />} label="By Carrier" value="carrier" />
            <FormControlLabel control={<Radio />} label="By Location" value="location" />
          </RadioGroup>
        </FormControl>
      ) : (
        <PillsSection
          name="authorizationBy"
          onTypeChange={handleTypeChange}
          options={CARRIER_AUTH_BY_OPTIONS}
          typeSelection={authorizationBy}
        />
      )}
      <Divider sx={dividerStyles} />
      {isCreateCarrierAuthEnabled ? (
        <>
          <Typography sx={{ ...authSetupTitleStyles, mt: 2 }} variant="body1">
            Authorization Setup
          </Typography>
          <Typography sx={{ ...authSetupDescriptionStyles, mt: 0.5 }} variant="body2">
            Setup your desired authorization
          </Typography>
          <Box display={sortedAffiliatesOptions.length > 1 ? 'block' : 'none'} pb={2} pt={1.5}>
            <MDropdown
              disableAutoLabelColor
              id="affiliate"
              items={sortedAffiliatesOptions}
              label="Affiliate"
              name="supplier"
              onChange={handleAffiliateChange}
              value={supplier?.value}
            />
          </Box>
          <Box pb={2} pt={1}>
            <TextField
              fullWidth
              label="Search"
              onChange={e => setDetailSearchTerm(e.target.value)}
              placeholder={isCarrierMode ? 'Search Carrier Name, SCAC or FEIN' : 'Search Locations'}
              size="small"
              slotProps={{
                input: {
                  endAdornment: quickFilterInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        edge="end"
                        onClick={handleClearSearch}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& input': {
                    padding: '14px 8px',
                  },
                },
              }}
              value={quickFilterInput}
              variant="outlined"
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
            <DataGridPremium
              columns={gridColumns}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableMultipleRowSelection
              filterModel={filterModel}
              hideFooter
              onRowSelectionModelChange={handleSelection}
              rowHeight={64}
              rows={rows}
              rowSelectionModel={selectedRowId ? [selectedRowId] : []}
              sortingOrder={['asc', 'desc', null]}
              sx={{
                '& .MuiDataGrid-main': { overflowX: 'hidden' },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'hidden',
                  overflowY: rows.length > 0 ? 'auto' : 'hidden',
                },
              }}
            />
          </Box>
        </>
      ) : (
        <>
          <Typography sx={{ mb: 3 }} variant="body1">
            Who would you like to activate authorizations for?
          </Typography>
          <Box display={sortedAffiliatesOptions.length > 1 ? 'block' : 'none'} pb={2}>
            <MDropdown
              id="affiliate"
              items={sortedAffiliatesOptions}
              label="Affiliate"
              name="supplier"
              onChange={handleAffiliateChange}
              value={supplier?.value}
            />
          </Box>
          <MAutocomplete
            error={!R.isEmpty(validation)}
            helperText={validation}
            id={authorizationBy}
            label={capitalize(authorizationBy)}
            loading={false}
            name={authorizationBy}
            onChange={R.flip(handleOnChange)}
            openOnFocus
            options={viewByListOptions}
            value={selectedViewByListOption?.value}
          />
        </>
      )}
    </Box>
  );
};

export default Detail;

Detail.propTypes = {
  authorizationBy: string.isRequired,
  currentSelection: shape({}),
  detailSearchTerm: string,
  fetchViewByList: func.isRequired,
  params: shape({}),
  setAuthorizationBy: func.isRequired,
  setDetailSearchTerm: func.isRequired,
  setHasUnsavedChanges: func.isRequired,
  setParams: func.isRequired,
  setSelectStepSearchTerm: func.isRequired,
  setWizardDetails: func.isRequired,
  supplier: shape({}),
  viewByList: arrayOf(shape({})),
  viewByListOptions: arrayOf(shape({})),
};

Detail.defaultProps = {
  currentSelection: {},
  detailSearchTerm: '',
  params: {},
  supplier: undefined,
  viewByList: [],
  viewByListOptions: [],
};
